import { t } from './i18n';
import { toastService } from './toast';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export interface PwaInstallState {
  available: boolean;
  mode: 'prompt' | 'ios' | 'manual' | 'installed';
}

type PwaInstallListener = (state: PwaInstallState) => void;

export class PwaInstallService {
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private listeners: PwaInstallListener[] = [];
  private initialized = false;
  private displayModeMediaQuery: MediaQueryList | null = null;

  private readonly handleBeforeInstallPrompt = (event: Event) => {
    event.preventDefault();
    this.deferredPrompt = event as BeforeInstallPromptEvent;
    this.notify();
  };

  private readonly handleAppInstalled = () => {
    this.deferredPrompt = null;
    toastService.success(t('pwa.installed'));
    this.notify();
  };

  private readonly handleDisplayModeChange = () => {
    this.notify();
  };

  init(): void {
    if (this.initialized || typeof window === 'undefined') {
      return;
    }

    this.initialized = true;
    window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt as EventListener);
    window.addEventListener('appinstalled', this.handleAppInstalled);

    if (typeof window.matchMedia === 'function') {
      this.displayModeMediaQuery = window.matchMedia('(display-mode: standalone)');
      this.displayModeMediaQuery.addEventListener?.('change', this.handleDisplayModeChange);
    }

    if ('serviceWorker' in navigator && window.isSecureContext) {
      navigator.serviceWorker.register('/service-worker.js').catch(() => undefined);
    }

    this.notify();
  }

  dispose(): void {
    if (!this.initialized || typeof window === 'undefined') {
      return;
    }

    window.removeEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt as EventListener);
    window.removeEventListener('appinstalled', this.handleAppInstalled);
    this.displayModeMediaQuery?.removeEventListener?.('change', this.handleDisplayModeChange);
    this.displayModeMediaQuery = null;
    this.initialized = false;
    this.deferredPrompt = null;
    this.listeners = [];
  }

  subscribe(listener: PwaInstallListener): () => void {
    this.listeners.push(listener);
    listener(this.installState);
    return () => {
      this.listeners = this.listeners.filter(existingListener => existingListener !== listener);
    };
  }

  get installState(): PwaInstallState {
    if (this.isStandalone()) {
      return {
        available: false,
        mode: 'installed',
      };
    }

    if (this.deferredPrompt) {
      return {
        available: true,
        mode: 'prompt',
      };
    }

    if (this.isIosSafari()) {
      return {
        available: true,
        mode: 'ios',
      };
    }

    return {
      available: true,
      mode: 'manual',
    };
  }

  async install(): Promise<void> {
    if (this.isStandalone()) {
      return;
    }

    if (this.deferredPrompt) {
      const prompt = this.deferredPrompt;
      this.deferredPrompt = null;
      await prompt.prompt();
      await prompt.userChoice.catch(() => undefined);
      this.notify();
      return;
    }

    if (this.isIosSafari()) {
      toastService.warning(t('pwa.installIosInstructions'), 7000);
      return;
    }

    toastService.warning(t('pwa.installManualInstructions'), 7000);
  }

  private isStandalone(): boolean {
    const displayModeStandalone = typeof window.matchMedia === 'function'
      ? window.matchMedia('(display-mode: standalone)').matches
      : false;
    const iosStandalone = Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
    return displayModeStandalone || iosStandalone;
  }

  private isIosSafari(): boolean {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isAppleMobileDevice = /iphone|ipad|ipod/.test(userAgent);
    const isSafari = /safari/.test(userAgent) && !/crios|fxios|edgios/.test(userAgent);
    return isAppleMobileDevice && isSafari;
  }

  private notify(): void {
    const state = this.installState;
    this.listeners.forEach(listener => listener(state));
  }
}

export const pwaInstallService = new PwaInstallService();
